import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Plus, Trash2, Mic, LayoutList, MapPin, UserCog } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/apiClient";
import { EmptyState } from "@/components/dashboard/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

const STATUS = ["upcoming", "live", "completed"];
const statusStyle = { upcoming: "bg-chart-3/10 text-chart-3", live: "bg-accent/10 text-accent", completed: "bg-muted text-muted-foreground" };
const empty = { title: "", speaker: "", moderator: "", room: "", description: "", start_time: "", end_time: "", status: "upcoming" };

export default function EventSessions() {
  const { event } = useOutletContext();
  const [sessions, setSessions] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = () => api.get(`/events/${event.id}/sessions`).then(({ data }) => setSessions(data));
  useEffect(() => { load(); }, [event.id]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/events/${event.id}/sessions`, { ...form, order: sessions.length });
      toast.success("Session added");
      setForm(empty); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const changeStatus = async (sid, status) => { await api.put(`/events/${event.id}/sessions/${sid}`, { status }); load(); };
  const remove = async (sid) => { await api.delete(`/events/${event.id}/sessions/${sid}`); toast.success("Session removed"); load(); };
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-display text-lg font-bold text-foreground">Sessions</h2>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-md" data-testid="add-session-btn"><Plus className="h-4 w-4 mr-1.5" /> Add session</Button></DialogTrigger>
          <DialogContent className="max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Add session</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4" data-testid="session-form">
              <div><Label>Title</Label><Input value={form.title} onChange={set("title")} required className="mt-1.5" data-testid="session-title" /></div>
              <div><Label>Description</Label><Textarea value={form.description} onChange={set("description")} rows={2} className="mt-1.5" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Speaker</Label><Input value={form.speaker} onChange={set("speaker")} className="mt-1.5" data-testid="session-speaker" /></div>
                <div><Label>Moderator</Label><Input value={form.moderator} onChange={set("moderator")} className="mt-1.5" /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Room</Label><Input value={form.room} onChange={set("room")} className="mt-1.5" /></div>
                <div><Label>Start</Label><Input type="time" value={form.start_time} onChange={set("start_time")} className="mt-1.5" /></div>
                <div><Label>End</Label><Input type="time" value={form.end_time} onChange={set("end_time")} className="mt-1.5" /></div>
              </div>
              <div><Label>Status</Label>
                <Select value={form.status} onValueChange={set("status")}>
                  <SelectTrigger className="mt-1.5 capitalize"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <DialogFooter><Button type="submit" className="rounded-md" data-testid="session-submit">Add session</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {sessions.length === 0 ? (
        <EmptyState icon={LayoutList} title="No sessions yet" description="Add sessions so attendees can ask questions and give feedback per talk." />
      ) : (
        <div className="space-y-2" data-testid="sessions-list">
          {sessions.map((s, i) => (
            <div key={s.id} className="rounded-xl border border-border bg-card p-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <span className="h-8 w-8 rounded-md bg-muted flex items-center justify-center text-sm font-bold text-muted-foreground shrink-0">{i + 1}</span>
                <div className="min-w-0">
                  <p className="font-medium text-foreground truncate">{s.title}</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-3 mt-0.5">
                    <span className="flex items-center gap-1"><Mic className="h-3 w-3" /> {s.speaker || "TBA"}</span>
                    {s.room && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {s.room}</span>}
                    {s.moderator && <span className="flex items-center gap-1"><UserCog className="h-3 w-3" /> {s.moderator}</span>}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Select value={s.status} onValueChange={(v) => changeStatus(s.id, v)}>
                  <SelectTrigger className={`h-8 w-32 text-xs capitalize ${statusStyle[s.status] || ""}`} data-testid={`session-status-${s.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS.map((st) => <SelectItem key={st} value={st} className="capitalize">{st}</SelectItem>)}</SelectContent>
                </Select>
                <button onClick={() => remove(s.id)} className="text-muted-foreground hover:text-destructive transition-colors" data-testid={`delete-session-${s.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
